import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';

/**
 * ProviderRateExpandedRow
 * Displays detailed service type configurations for a provider
 * 
 * @param {Object} provider - Provider object with nested service rates
 * @param {Array} activeServiceTypes - All active service types in the system
 */
function ProviderRateExpandedRow({ provider, activeServiceTypes = [] }) {
  const navigate = useNavigate();
  
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return `$${parseFloat(value).toFixed(2)}`;
  };
  
  const handleEditProvider = () => {
    navigate(`/provider/edit/${provider.id}`);
  };
  
  // Create a map of service type configs for quick lookup
  const configMap = {};
  (provider.provider_service_rates || []).forEach(config => {
    configMap[config.service_type_id] = config;
  });
  
  // Render rate details based on service type and rate type
  const renderRateDetails = (serviceType, config) => {
    if (!config) {
      return (
        <TableCell colSpan={2}>
          <Alert severity="warning" sx={{ py: 0.5 }}>
            Not Configured
          </Alert>
        </TableCell>
      );
    }
    
    const isInitialServiceCall = serviceType.name === 'Initial Service Call';
    const rateType = config.rate_type;
    
    return (
      <>
        <TableCell>
          <Chip 
            label={rateType} 
            size="small" 
            color={rateType === 'FLAT' ? 'primary' : 'secondary'}
          />
        </TableCell>
        <TableCell>
          {rateType === 'HOURLY' ? (
            <Box>
              <Typography variant="body2">
                <strong>Hourly Rate:</strong> {formatCurrency(config.hourly_rate)}
              </Typography>
            </Box>
          ) : isInitialServiceCall ? (
            // Tiered rate structure for Initial Service Call
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Per Board:</strong> {formatCurrency(config.per_board)} | 
                <strong> Per Location:</strong> {formatCurrency(config.per_location)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Units:</strong> 
                {' '}1u: {formatCurrency(config.rate_1_unit)},
                {' '}2u: {formatCurrency(config.rate_2_units)},
                {' '}3u: {formatCurrency(config.rate_3_units)},
                {' '}4u: {formatCurrency(config.rate_4_units)},
                {' '}5u: {formatCurrency(config.rate_5_units)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>More:</strong> 
                {' '}6u: {formatCurrency(config.rate_6_units)},
                {' '}7u: {formatCurrency(config.rate_7_units)},
                {' '}8u: {formatCurrency(config.rate_8_units)},
                {' '}9u: {formatCurrency(config.rate_9_units)},
                {' '}10u: {formatCurrency(config.rate_10_units)}
              </Typography>
              <Typography variant="body2">
                <strong>Refusal:</strong> {formatCurrency(config.refusal_rate)}
              </Typography>
            </Box>
          ) : (
            // Simple flat rate for other service types
            <Box>
              <Typography variant="body2">
                <strong>Completion:</strong> {formatCurrency(config.flat_rate)} | 
                <strong> Refusal:</strong> {formatCurrency(config.refusal_rate)}
              </Typography>
            </Box>
          )}
        </TableCell>
      </>
    );
  };
  
  return (
    <TableRow>
      <TableCell colSpan={6} sx={{ py: 0, backgroundColor: '#f5f5f5' }}>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Service Type Configurations for {provider.name}
            </Typography>
            <Tooltip title="Edit Provider">
              <IconButton onClick={handleEditProvider} color="primary" size="small">
                <EditIcon />
              </IconButton>
            </Tooltip>
          </Box>
          
          {activeServiceTypes.length === 0 ? (
            <Alert severity="info">No active service types found in the system.</Alert>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Service Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Rate Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '60%' }}>Rate Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeServiceTypes.map(serviceType => {
                    const config = configMap[serviceType.id];
                    return (
                      <TableRow 
                        key={serviceType.id}
                        sx={{ 
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                          backgroundColor: config ? 'inherit' : '#fff3e0'
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {serviceType.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {serviceType.code}
                          </Typography>
                        </TableCell>
                        {renderRateDetails(serviceType, config)}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
}

export default ProviderRateExpandedRow;

